import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  MagicWand, Article, YoutubeLogo, TextT, FloppyDisk, CheckCircle,
  CalendarPlus, PaperPlaneTilt, Scissors, Sparkle, ArrowLeft, Copy,
  UploadSimple, Clock, SpinnerGap, DownloadSimple, Image as ImageIcon, Trash, FilmSlate, SpeakerHigh, Translate, Microphone,
} from "@phosphor-icons/react";
import AppLayout from "@/components/AppLayout";
import VisualGenerator, { downloadDataUrl } from "@/components/VisualGenerator";
import VoiceRecorder from "@/components/VoiceRecorder";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/lib/api";
import analytics from "@/lib/analytics";
import { useWorkspace } from "@/context/WorkspaceContext";
import { PLATFORM_META, StatusBadge } from "@/components/postMeta";

const SOURCES = [
  { id: "paste", label: "Paste text", icon: TextT },
  { id: "youtube", label: "YouTube / URL", icon: YoutubeLogo },
  { id: "media", label: "Audio / Video", icon: UploadSimple },
  { id: "voice", label: "Voice memo", icon: Microphone },
];
const PLATFORM_ORDER = ["linkedin", "twitter", "instagram", "facebook", "newsletter"];

const VOICE_LANGS = [
  { code: "auto", label: "Auto-detect" },
  { code: "en", label: "English" },
  { code: "es", label: "Spanish" },
  { code: "fr", label: "French" },
  { code: "de", label: "German" },
  { code: "pt", label: "Portuguese" },
  { code: "it", label: "Italian" },
  { code: "nl", label: "Dutch" },
  { code: "hi", label: "Hindi" },
  { code: "ja", label: "Japanese" },
  { code: "ko", label: "Korean" },
  { code: "zh", label: "Chinese" },
  { code: "ru", label: "Russian" },
  { code: "ar", label: "Arabic" },
  { code: "tr", label: "Turkish" },
  { code: "pl", label: "Polish" },
];

function MediaPickerRow({ activeId, onPick }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!activeId) return;
    api.get(`/media?workspace_id=${activeId}`)
      .then((r) => setItems((r.data || []).filter((m) => (m.content_type || "").startsWith("image/"))))
      .catch(() => {});
  }, [activeId]);
  if (items.length === 0) return null;
  return (
    <div className="border-t border-border pt-3 mt-1" data-testid="media-picker">
      <p className="text-xs font-mono uppercase text-muted-foreground mb-2">Or reuse from Media Library</p>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {items.map((m) => (
          <button key={m.id} type="button" data-testid={`media-pick-${m.id}`} onClick={() => onPick(m.id)}
            className="shrink-0 w-16 h-16 rounded-md border border-border overflow-hidden hover:ring-2 hover:ring-primary transition-all">
            <MediaPickThumb id={m.id} />
          </button>
        ))}
      </div>
    </div>
  );
}

function MediaPickThumb({ id }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let revoke;
    api.get(`/media/${id}/raw`, { responseType: "blob" }).then((r) => {
      const u = URL.createObjectURL(r.data); revoke = u; setUrl(u);
    }).catch(() => {});
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [id]);
  return url ? <img src={url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full bg-secondary/40 animate-pulse" />;
}

function ModelSelect({ value, onChange }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-[180px]" data-testid="model-select">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="gpt" data-testid="model-gpt">GPT-5.2 (OpenAI)</SelectItem>
        <SelectItem value="claude" data-testid="model-claude">Claude Sonnet 4.6</SelectItem>
        <SelectItem value="gemini" data-testid="model-gemini">Gemini 3.1 Pro</SelectItem>
      </SelectContent>
    </Select>
  );
}

export default function Studio() {
  const { contentId } = useParams();
  const navigate = useNavigate();
  const { activeId, active, refresh } = useWorkspace();

  const [source, setSource] = useState("paste");
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [model, setModel] = useState("gpt");
  const [busy, setBusy] = useState(false);
  const [transcribing, setTranscribing] = useState(false);

  const [content, setContent] = useState(null);
  const [posts, setPosts] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [scheduling, setScheduling] = useState(null);
  const [scheduleAt, setScheduleAt] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [visualFor, setVisualFor] = useState(null);
  const [autoCover, setAutoCover] = useState(true);
  const [looks, setLooks] = useState([]);
  const [coverLookId, setCoverLookId] = useState("auto");
  const [regenning, setRegenning] = useState(false);
  const [clipDl, setClipDl] = useState(null);
  const [clipRatio, setClipRatio] = useState("4:5");
  const [clipVoice, setClipVoice] = useState("auto");
  const [clipCaptions, setClipCaptions] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [packLangs, setPackLangs] = useState([]);
  const [packLangOpen, setPackLangOpen] = useState(false);
  const [packLangBusy, setPackLangBusy] = useState(false);
  const [postView, setPostView] = useState({});
  const [voiceFor, setVoiceFor] = useState(null);
  const [voiceProvider, setVoiceProvider] = useState("openai");
  const [openaiVoice, setOpenaiVoice] = useState("nova");
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [elevenReady, setElevenReady] = useState(false);
  const [translateFor, setTranslateFor] = useState(null);
  const [languages, setLanguages] = useState([]);
  const [targetLang, setTargetLang] = useState("");
  const [translating, setTranslating] = useState(false);
  const [translation, setTranslation] = useState(null);
  const [voiceLang, setVoiceLang] = useState("");

  const load = useCallback(async () => {
    if (!contentId) return;
    try {
      const { data } = await api.get(`/content/${contentId}`);
      setContent(data);
      setPosts(data.posts || []);
      const d = {};
      (data.posts || []).forEach((p) => (d[p.id] = p.body));
      setDrafts(d);
    } catch {
      toast.error("Content not found");
      navigate("/library");
    }
  }, [contentId, navigate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!activeId) return;
    api.get(`/brand-templates?workspace_id=${activeId}`).then((r) => {
      setLooks(r.data);
      const def = r.data.find((t) => t.is_default);
      if (def) setCoverLookId(def.id);
    }).catch(() => {});
  }, [activeId]);

  useEffect(() => {
    api.get("/voices").then((r) => setElevenReady(!!r.data.elevenlabs_configured)).catch(() => {});
    api.get("/languages").then((r) => { if (r.data.configured) setLanguages(r.data.languages || []); }).catch(() => {});
    api.get("/music-moods").then((r) => setMusicMoods(r.data.moods || [])).catch(() => {});
  }, []);

  const openTranslate = (id) => {
    setTranslation(null);
    setTargetLang("");
    setTranslateFor(id);
  };

  const runTranslate = async () => {
    if (!targetLang) return;
    setTranslating(true);
    try {
      const { data } = await api.post(`/posts/${translateFor}/translate`, { target_lang: targetLang });
      setTranslation(data);
      analytics.capture("post_translated", { target_lang: targetLang });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Translation failed");
    } finally {
      setTranslating(false);
    }
  };

  const generate = async (opts = {}) => {
    const ttl = (opts.title ?? title).trim();
    const body = (opts.text ?? text).trim();
    if (!ttl || !body) { toast.error("Add a title and some content"); return; }
    if (!activeId) { toast.error("No workspace selected"); return; }
    setBusy(true);
    try {
      const { data: ct } = await api.post("/content", {
        workspace_id: activeId, source_type: opts.source ?? source, title: ttl, source_url: url, content: body,
      });
      await api.post(`/content/${ct.id}/repurpose`, {
        model, auto_cover: autoCover, cover_look_id: coverLookId === "auto" ? null : coverLookId,
        translate_to: opts.translateTo || null,
      });
      await refresh();
      toast.success("Content pack generated");
      analytics.capture("content_repurposed", { model, source_type: opts.source ?? source, auto_cover: autoCover });
      navigate(`/studio/${ct.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generation failed");
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async () => {
    setBusy(true);
    try {
      await api.post(`/content/${contentId}/repurpose`, { model, auto_cover: false });
      await refresh();
      await load();
      toast.success("Regenerated with " + (model === "gpt" ? "GPT-5.2" : model === "claude" ? "Claude" : "Gemini"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const savePost = async (id) => {
    try {
      await api.put(`/posts/${id}`, { body: drafts[id] });
      setPosts((ps) => ps.map((p) => (p.id === id ? { ...p, body: drafts[id] } : p)));
      toast.success("Saved");
    } catch { toast.error("Save failed"); }
  };

  const setStatus = async (id, action) => {
    try {
      const { data } = await api.post(`/posts/${id}/${action}`);
      const status = action === "approve" ? "approved" : "published";
      setPosts((ps) => ps.map((p) => (p.id === id ? { ...p, status } : p)));
      if (action === "approve") toast.success("Approved");
      else {
        toast.success(data?.simulated ? "Published (simulated)" : "Published live 🎉", { description: data?.detail });
        analytics.capture("post_published", { simulated: !!data?.simulated });
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const doSchedule = async () => {
    if (!scheduleAt) return;
    try {
      const iso = new Date(scheduleAt).toISOString();
      await api.post(`/posts/${scheduling}/schedule`, { scheduled_at: iso });
      setPosts((ps) => ps.map((p) => (p.id === scheduling ? { ...p, status: "scheduled", scheduled_at: iso } : p)));
      toast.success("Scheduled");
      setScheduling(null); setScheduleAt("");
    } catch { toast.error("Schedule failed"); }
  };

  const copy = (body) => { navigator.clipboard.writeText(body); toast.success("Copied"); };

  const fetchYoutubeTranscript = async () => {
    if (!url.trim()) { toast.error("Paste a YouTube URL first"); return; }
    setTranscribing(true);
    try {
      const { data } = await api.post("/transcribe/youtube", { url });
      setText(data.text);
      if (!title.trim()) setTitle("YouTube transcript");
      toast.success("Transcript fetched");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not fetch transcript");
    } finally {
      setTranscribing(false);
    }
  };

  const uploadMedia = async (file, language = "") => {
    if (!file) return null;
    setTranscribing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (language) fd.append("language", language);
      const { data } = await api.post("/transcribe/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setText(data.text);
      if (!title.trim()) setTitle(file.name.replace(/\.[^.]+$/, ""));
      toast.success("Audio transcribed");
      return data.text;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transcription failed");
      return null;
    } finally {
      setTranscribing(false);
    }
  };

  const voiceMemoGenerate = async (file) => {
    const transcript = await uploadMedia(file, voiceLang);
    if (!transcript || !transcript.trim()) return;
    const autoTitle = title.trim() || `Voice memo — ${new Date().toLocaleDateString()}`;
    setTitle(autoTitle);
    await generate({ text: transcript, title: autoTitle, source: "voice", translateTo: voiceLang });
  };

  const openSchedule = async (id) => {
    setScheduling(id); setScheduleAt(""); setSuggestions([]);
    try {
      const { data } = await api.get(`/posts/${id}/suggested-times`);
      setSuggestions(data.suggestions || []);
    } catch { /* non-blocking */ }
  };

  const attachVisual = async (imageUrl) => {
    if (!visualFor) return;
    try {
      await api.post(`/posts/${visualFor}/attach-image`, { image_url: imageUrl });
      setPosts((ps) => ps.map((p) => (p.id === visualFor ? { ...p, image_url: imageUrl } : p)));
      toast.success("Visual attached to post");
      setVisualFor(null);
    } catch { toast.error("Could not attach image"); }
  };

  const attachMedia = async (mediaId) => {
    if (!visualFor) return;
    try {
      const { data } = await api.post(`/posts/${visualFor}/attach-image`, { media_id: mediaId });
      setPosts((ps) => ps.map((p) => (p.id === visualFor ? { ...p, image_url: data.image_url } : p)));
      toast.success("Asset attached to post");
      setVisualFor(null);
    } catch { toast.error("Could not attach asset"); }
  };

  const removeVisual = async (id) => {
    try {
      await api.post(`/posts/${id}/attach-image`, { image_url: null });
      setPosts((ps) => ps.map((p) => (p.id === id ? { ...p, image_url: null } : p)));
      toast.success("Visual removed");
    } catch { toast.error("Could not remove image"); }
  };

  const regenerateCover = async () => {
    setRegenning(true);
    try {
      const { data } = await api.post(`/content/${contentId}/regenerate-cover`, {});
      setPosts((ps) => ps.map((p) => ({ ...p, image_url: data.cover_image })));
      toast.success("New cover generated & applied to all posts");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not regenerate cover");
    } finally {
      setRegenning(false);
    }
  };

  const [aiJob, setAiJob] = useState(null);
  const [aiMode, setAiMode] = useState("text");
  const [aiMusic, setAiMusic] = useState("");
  const [musicMoods, setMusicMoods] = useState([]);
  const [aiPreview, setAiPreview] = useState(null);
  const downloadClip = async (index) => {
    setClipDl(index);
    try {
      const vp = clipVoice === "auto" ? "" : clipVoice;
      const res = await api.get(`/content/${contentId}/clips/${index}/video?ratio=${encodeURIComponent(clipRatio)}&voice_post_id=${vp}&captions=${clipCaptions}`, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl; a.download = `clip-${index + 1}-${clipRatio.replace(":", "x")}.mp4`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success("Clip downloaded");
    } catch (e) {
      toast.error("Could not render clip video");
    } finally {
      setClipDl(null);
    }
  };

  const translatePack = async () => {
    if (packLangs.length === 0) { toast.error("Pick at least one language"); return; }
    setPackLangBusy(true);
    try {
      const { data } = await api.post(`/content/${contentId}/translate-pack`, { languages: packLangs });
      await refresh();
      analytics.capture("pack_multilang", { languages: packLangs });
      toast.success(`Translated into ${data.languages.length} language${data.languages.length > 1 ? "s" : ""}`);
      setPackLangOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Translation failed");
    } finally {
      setPackLangBusy(false);
    }
  };

  const [schedTransBusy, setSchedTransBusy] = useState(false);
  const scheduleTranslations = async () => {
    setSchedTransBusy(true);
    try {
      const { data } = await api.post(`/content/${contentId}/schedule-translations`);
      analytics.capture("multilang_scheduled", { count: data.scheduled.length });
      toast.success(`Scheduled ${data.scheduled.length} translated post${data.scheduled.length > 1 ? "s" : ""} at regional best times`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not schedule translations");
    } finally {
      setSchedTransBusy(false);
    }
  };

  const aiClip = async (index) => {
    setAiJob(index);
    const vp = clipVoice === "auto" ? "" : clipVoice;
    try {
      const { data } = await api.post(`/content/${contentId}/clips/${index}/ai-video?ratio=${encodeURIComponent(clipRatio)}&captions=${clipCaptions}&voice_post_id=${vp}&mode=${aiMode}&music=${aiMusic}`);
      toast.info(aiMode === "image" ? "Animating your cover into b-roll… this can take a minute" : "Generating real AI b-roll footage… this can take a minute");
      const jobId = data.job_id;
      const poll = async () => {
        try {
          const { data: job } = await api.get(`/clip-jobs/${jobId}`);
          if (job.status === "done") {
            const res = await api.get(`/media/${job.media_id}/raw`, { responseType: "blob" });
            const blobUrl = URL.createObjectURL(res.data);
            setAiPreview({ url: blobUrl, index });
            toast.success("AI video ready — saved to your Media Library");
            analytics.capture("ai_clip_generated", { ratio: clipRatio, mode: aiMode });
            setAiJob(null);
          } else if (job.status === "failed") {
            toast.error(job.error || "AI video generation failed");
            setAiJob(null);
          } else {
            setTimeout(poll, 5000);
          }
        } catch {
          toast.error("Lost track of the AI video job");
          setAiJob(null);
        }
      };
      setTimeout(poll, 5000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start AI video");
      setAiJob(null);
    }
  };

  const [aiBatch, setAiBatch] = useState(null);
  const aiClipAll = async () => {
    setAiBatch({ done: 0, total: 0 });
    const vp = clipVoice === "auto" ? "" : clipVoice;
    try {
      const { data } = await api.post(`/content/${contentId}/clips/ai-video-all?ratio=${encodeURIComponent(clipRatio)}&captions=${clipCaptions}&voice_post_id=${vp}&mode=${aiMode}&music=${aiMusic}`);
      setAiBatch({ done: 0, total: data.total });
      toast.info(`Generating AI video for ${data.total} clips… saved to Media Library as they finish`);
      const jobId = data.job_id;
      const poll = async () => {
        try {
          const { data: job } = await api.get(`/clip-jobs/${jobId}`);
          setAiBatch({ done: job.done_count || 0, total: job.total });
          if (job.status === "done") {
            const ok = (job.results || []).filter((r) => r.media_id).length;
            toast.success(`Saved ${ok} AI clip${ok !== 1 ? "s" : ""} to your Media Library`);
            analytics.capture("ai_clips_batch", { count: ok, mode: aiMode });
            setAiBatch(null);
          } else if (job.status === "failed") {
            toast.error(job.error || "AI video generation failed");
            setAiBatch(null);
          } else {
            setTimeout(poll, 6000);
          }
        } catch {
          toast.error("Lost track of the batch job");
          setAiBatch(null);
        }
      };
      setTimeout(poll, 6000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start batch");
      setAiBatch(null);
    }
  };

  const exportPack = async () => {
    setExporting(true);
    toast.info("Building your pack… clips can take a minute to render");
    try {
      const res = await api.get(`/content/${contentId}/export?ratio=${encodeURIComponent(clipRatio)}&captions=${clipCaptions}`, { responseType: "blob", timeout: 300000 });
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const safe = (content.title || "pack").replace(/[^a-zA-Z0-9]+/g, "-").slice(0, 40);
      a.href = blobUrl; a.download = `${safe}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success("Pack downloaded");
      analytics.capture("pack_exported", { ratio: clipRatio, captions: clipCaptions });
    } catch (e) {
      toast.error("Could not export pack");
    } finally {
      setExporting(false);
    }
  };

  const generateVoiceover = async () => {
    setVoiceBusy(true);
    try {
      const voice = voiceProvider === "openai" ? openaiVoice : "";
      const { data } = await api.post(`/posts/${voiceFor}/voiceover`, { provider: voiceProvider, voice });
      setPosts((ps) => ps.map((p) => (p.id === voiceFor ? { ...p, audio_url: data.audio_url } : p)));
      toast.success("Voiceover generated");
      setVoiceFor(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Voiceover failed");
    } finally {
      setVoiceBusy(false);
    }
  };

  // ---------- INGEST VIEW ----------
  if (!contentId) {
    return (
      <AppLayout>
        <div className="max-w-3xl">
          <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight">New Repurpose</h1>
          <p className="text-muted-foreground mt-2">Drop in one long piece of content — we'll turn it into a full pack.</p>

          <div className="mt-8 rounded-lg border border-border bg-card p-6 md:p-8 space-y-6">
            <div>
              <Label className="mb-2 block">Source type</Label>
              <div className="grid grid-cols-3 gap-3">
                {SOURCES.map((s) => (
                  <button
                    key={s.id}
                    data-testid={`source-${s.id}`}
                    onClick={() => setSource(s.id)}
                    className={`flex flex-col items-center gap-2 p-4 rounded-md border transition-colors ${
                      source === s.id ? "border-primary bg-primary/5 text-primary" : "border-border hover:bg-accent"
                    }`}
                  >
                    <s.icon size={22} weight={source === s.id ? "fill" : "regular"} />
                    <span className="text-xs font-medium text-center">{s.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label htmlFor="title">Title</Label>
              <Input id="title" data-testid="ingest-title" value={title} onChange={(e) => setTitle(e.target.value)}
                className="mt-1.5" placeholder="e.g. Why consistency beats motivation" />
            </div>

            {source === "youtube" && (
              <div>
                <Label htmlFor="url">YouTube / content URL</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input id="url" data-testid="ingest-url" value={url} onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://youtube.com/watch?v=…" />
                  <Button type="button" variant="outline" data-testid="fetch-transcript-btn"
                    onClick={fetchYoutubeTranscript} disabled={transcribing}>
                    {transcribing ? <SpinnerGap size={16} className="animate-spin" /> : <><DownloadSimple size={16} className="mr-1.5" /> Fetch transcript</>}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">
                  We auto-fetch captions when available. No captions? Use Audio / Video upload instead.
                </p>
              </div>
            )}

            {source === "media" && (
              <div>
                <Label>Audio / Video file</Label>
                <label
                  data-testid="media-dropzone"
                  className="mt-1.5 flex flex-col items-center justify-center gap-2 p-8 rounded-md border border-dashed border-border hover:bg-accent transition-colors cursor-pointer text-center"
                >
                  {transcribing ? (
                    <><SpinnerGap size={26} className="animate-spin text-primary" /><span className="text-sm">Transcribing with Whisper…</span></>
                  ) : (
                    <><UploadSimple size={26} className="text-primary" /><span className="text-sm font-medium">Click to upload audio or video</span>
                    <span className="text-xs text-muted-foreground">mp3, m4a, wav, mp4, webm · max 25MB</span></>
                  )}
                  <input type="file" className="hidden" data-testid="media-input"
                    accept=".mp3,.m4a,.wav,.mp4,.webm,.mpeg,.mpga,audio/*,video/*"
                    onChange={(e) => uploadMedia(e.target.files?.[0])} disabled={transcribing} />
                </label>
              </div>
            )}

            {source === "voice" && (
              <div>
                <Label>Voice memo</Label>
                <div className="mt-1.5 mb-2 flex items-center gap-2">
                  <Label className="text-xs text-muted-foreground whitespace-nowrap">Spoken language</Label>
                  <Select value={voiceLang || "auto"} onValueChange={(v) => setVoiceLang(v === "auto" ? "" : v)}>
                    <SelectTrigger className="h-8 w-[190px]" data-testid="voice-lang-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {VOICE_LANGS.map((l) => (
                        <SelectItem key={l.code} value={l.code} data-testid={`voice-lang-${l.code}`}>{l.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <VoiceRecorder onTranscribe={(f) => uploadMedia(f, voiceLang)} onGenerate={voiceMemoGenerate} busy={transcribing || busy} />
              </div>
            )}

            <div>
              <Label htmlFor="text">Source content / transcript</Label>
              <Textarea id="text" data-testid="ingest-content" value={text} onChange={(e) => setText(e.target.value)}
                className="mt-1.5 min-h-[200px] font-mono text-sm leading-relaxed" placeholder="Paste your transcript, blog post, or notes here…" />
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-2 border-t border-border">
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <Label className="whitespace-nowrap">AI model</Label>
                  <ModelSelect value={model} onChange={setModel} />
                </div>
                <div className="flex items-center gap-2.5">
                  <Switch id="auto-cover" data-testid="auto-cover-toggle" checked={autoCover} onCheckedChange={setAutoCover} />
                  <Label htmlFor="auto-cover" className="text-sm text-muted-foreground cursor-pointer">
                    Auto-generate a cover image
                  </Label>
                </div>
                {autoCover && (
                  <div className="flex items-center gap-2.5">
                    <Label className="text-sm text-muted-foreground whitespace-nowrap">Brand look</Label>
                    <Select value={coverLookId} onValueChange={setCoverLookId}>
                      <SelectTrigger className="w-[200px]" data-testid="cover-look-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto" data-testid="cover-look-auto">Auto (default look)</SelectItem>
                        {looks.map((l) => (
                          <SelectItem key={l.id} value={l.id} data-testid={`cover-look-${l.id}`}>
                            {l.name}{l.is_default ? " ★" : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              <Button size="lg" data-testid="generate-btn" onClick={generate} disabled={busy}>
                {busy ? (
                  <><span className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin mr-2" /> Generating…</>
                ) : (
                  <><MagicWand size={18} weight="fill" className="mr-2" /> Generate content pack</>
                )}
              </Button>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  // ---------- EDITOR VIEW ----------
  if (!content) {
    return <AppLayout><div className="flex justify-center py-24"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div></AppLayout>;
  }

  const ordered = PLATFORM_ORDER.map((pl) => posts.find((p) => p.platform === pl)).filter(Boolean);

  return (
    <AppLayout>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" data-testid="studio-back" onClick={() => navigate("/library")}><ArrowLeft size={18} /></Button>
          <div className="min-w-0">
            <h1 className="font-heading font-bold text-xl md:text-2xl tracking-tight truncate">{content.title}</h1>
            <p className="text-xs font-mono uppercase text-muted-foreground">{content.source_type} · via {content.model_used === "claude" ? "Claude Sonnet 4.6" : content.model_used === "gemini" ? "Gemini 3.1 Pro" : "GPT-5.2"}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <ModelSelect value={model} onChange={setModel} />
          <Button variant="outline" data-testid="regenerate-btn" onClick={regenerate} disabled={busy}>
            <Sparkle size={16} weight="fill" className="mr-1.5" /> Regenerate
          </Button>
          <Button variant="outline" data-testid="regen-cover-btn" onClick={regenerateCover} disabled={regenning}>
            {regenning ? <SpinnerGap size={16} className="animate-spin mr-1.5" /> : <ImageIcon size={16} className="mr-1.5" />} New cover
          </Button>
          <Button data-testid="export-pack-btn" onClick={exportPack} disabled={exporting}>
            {exporting ? <SpinnerGap size={16} className="animate-spin mr-1.5" /> : <DownloadSimple size={16} className="mr-1.5" />} Download pack
          </Button>
          <Button variant="outline" data-testid="translate-pack-btn" onClick={() => setPackLangOpen(true)}>
            <Translate size={16} className="mr-1.5" /> Translate pack
          </Button>
          {posts.some((p) => p.translations && Object.keys(p.translations).length > 0) && (
            <Button variant="outline" data-testid="schedule-translations-btn" onClick={scheduleTranslations} disabled={schedTransBusy}>
              {schedTransBusy ? <SpinnerGap size={16} className="animate-spin mr-1.5" /> : <Clock size={16} className="mr-1.5" />} Schedule translations
            </Button>
          )}
        </div>
      </div>

      <ResizablePanelGroup direction="horizontal" className="rounded-lg border border-border bg-card min-h-[70vh]">
        {/* LEFT: source */}
        <ResizablePanel defaultSize={38} minSize={25}>
          <div className="p-6 h-full flex flex-col">
            <div className="flex items-center gap-2 mb-4">
              <Article size={18} className="text-muted-foreground" />
              <span className="text-sm font-mono uppercase text-muted-foreground">Source content</span>
            </div>
            {content.source_url && (
              <a href={content.source_url} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline mb-3 truncate" data-testid="source-url-link">{content.source_url}</a>
            )}
            <div className="flex-1 overflow-y-auto max-h-[70vh] rounded-md bg-background border border-border p-4">
              <p className="text-sm leading-relaxed whitespace-pre-wrap text-foreground/90">{content.content}</p>
            </div>

            {/* Clips */}
            {content.clips?.length > 0 && (
              <div className="mt-4">
                <div className="flex items-center gap-2 mb-2">
                  <Scissors size={16} className="text-primary" />
                  <span className="text-sm font-mono uppercase text-muted-foreground">Clip suggestions</span>
                </div>
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <Select value={clipRatio} onValueChange={setClipRatio}>
                    <SelectTrigger className="h-8 w-[150px]" data-testid="clip-ratio-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="9:16" data-testid="clip-ratio-9x16">9:16 · Reels/TikTok</SelectItem>
                      <SelectItem value="4:5" data-testid="clip-ratio-4x5">4:5 · Feed</SelectItem>
                      <SelectItem value="1:1" data-testid="clip-ratio-1x1">1:1 · Square</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={clipVoice} onValueChange={setClipVoice}>
                    <SelectTrigger className="h-8 w-[190px]" data-testid="clip-voice-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto" data-testid="clip-voice-auto">Voiceover: Auto / none</SelectItem>
                      {posts.filter((p) => p.audio_url).map((p) => (
                        <SelectItem key={p.id} value={p.id} data-testid={`clip-voice-${p.platform}`} className="capitalize">
                          Voice: {p.platform}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex items-center gap-2 ml-1">
                    <Switch id="clip-captions" checked={clipCaptions} onCheckedChange={setClipCaptions} data-testid="clip-captions-toggle" />
                    <Label htmlFor="clip-captions" className="text-xs text-muted-foreground cursor-pointer">Captions</Label>
                  </div>
                  <Select value={aiMode} onValueChange={setAiMode}>
                    <SelectTrigger className="h-8 w-[170px]" data-testid="ai-mode-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="text" data-testid="ai-mode-text">AI style: B-roll</SelectItem>
                      <SelectItem value="image" data-testid="ai-mode-image">AI style: Animate cover</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={aiMusic || "none"} onValueChange={(v) => setAiMusic(v === "none" ? "" : v)}>
                    <SelectTrigger className="h-8 w-[160px]" data-testid="ai-music-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none" data-testid="ai-music-none">Music: None</SelectItem>
                      {musicMoods.map((m) => (
                        <SelectItem key={m} value={m} data-testid={`ai-music-${m}`} className="capitalize">Music: {m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="secondary" size="sm" className="h-8" data-testid="ai-clip-all-btn"
                    onClick={aiClipAll} disabled={!!aiBatch}>
                    {aiBatch
                      ? <><SpinnerGap size={14} className="animate-spin mr-1" /> {aiBatch.done}/{aiBatch.total || "…"}</>
                      : <><Sparkle size={14} weight="fill" className="mr-1" /> AI video · all clips</>}
                  </Button>
                </div>
                <div className="space-y-2 max-h-[24vh] overflow-y-auto">
                  {content.clips.map((c, i) => (
                    <div key={i} data-testid={`clip-${i}`} className="rounded-md border border-border p-3 bg-background">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="secondary" className="font-mono text-xs">{c.timestamp}</Badge>
                        <span className="text-sm font-medium truncate">{c.title}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{c.caption}</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <Button variant="outline" size="sm" className="h-7" data-testid={`download-clip-${i}`}
                          onClick={() => downloadClip(i)} disabled={clipDl === i}>
                          {clipDl === i
                            ? <><SpinnerGap size={13} className="animate-spin mr-1" /> Rendering…</>
                            : <><FilmSlate size={13} className="mr-1" /> Download video</>}
                        </Button>
                        <Button variant="secondary" size="sm" className="h-7" data-testid={`ai-clip-${i}`}
                          onClick={() => aiClip(i)} disabled={aiJob === i}>
                          {aiJob === i
                            ? <><SpinnerGap size={13} className="animate-spin mr-1" /> Generating AI video…</>
                            : <><Sparkle size={13} weight="fill" className="mr-1" /> AI video</>}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </ResizablePanel>

        <ResizableHandle withHandle />

        {/* RIGHT: generated posts */}
        <ResizablePanel defaultSize={62} minSize={40}>
          <div className="p-6 h-full">
            <Tabs defaultValue={ordered[0]?.platform || "linkedin"} className="h-full flex flex-col">
              <TabsList className="w-full justify-start flex-wrap h-auto gap-1">
                {ordered.map((p) => {
                  const meta = PLATFORM_META[p.platform];
                  return (
                    <TabsTrigger key={p.id} value={p.platform} data-testid={`tab-${p.platform}`} className="gap-1.5">
                      <meta.icon size={15} weight="fill" /> {meta.label}
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              {ordered.map((p) => (
                <TabsContent key={p.id} value={p.platform} className="flex-1 mt-4">
                  <div className="flex items-center justify-between mb-3">
                    <StatusBadge status={p.status} />
                    <div className="flex items-center gap-1.5">
                      <Button variant="ghost" size="sm" data-testid={`copy-${p.platform}`} onClick={() => copy(drafts[p.id])}><Copy size={15} className="mr-1" /> Copy</Button>
                      <Button variant="outline" size="sm" data-testid={`save-${p.platform}`} onClick={() => savePost(p.id)}><FloppyDisk size={15} className="mr-1" /> Save</Button>
                    </div>
                  </div>

                  {p.translations && Object.keys(p.translations).length > 0 && (
                    <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid={`lang-switch-${p.platform}`}>
                      <span className="text-xs text-muted-foreground mr-1">View:</span>
                      {["original", ...Object.keys(p.translations)].map((code) => {
                        const active = (postView[p.id] || "original") === code;
                        return (
                          <button key={code} type="button" data-testid={`lang-pill-${p.platform}-${code}`}
                            onClick={() => setPostView((v) => ({ ...v, [p.id]: code }))}
                            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${active ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-accent"}`}>
                            {code === "original" ? "Original" : code}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {(postView[p.id] && postView[p.id] !== "original") ? (
                    <div className="relative">
                      <Textarea data-testid={`editor-${p.platform}-${postView[p.id]}`} readOnly
                        value={p.translations[postView[p.id]] || ""}
                        className="min-h-[320px] font-sans text-sm leading-relaxed bg-secondary/30" />
                      <Button variant="ghost" size="sm" className="absolute top-2 right-2"
                        data-testid={`copy-lang-${p.platform}`} onClick={() => copy(p.translations[postView[p.id]])}>
                        <Copy size={15} className="mr-1" /> Copy
                      </Button>
                    </div>
                  ) : (
                    <Textarea
                      data-testid={`editor-${p.platform}`}
                      value={drafts[p.id] ?? ""}
                      onChange={(e) => setDrafts((d) => ({ ...d, [p.id]: e.target.value }))}
                      className="min-h-[320px] font-sans text-sm leading-relaxed"
                    />
                  )}

                  {p.image_url && (
                    <div className="mt-3 flex items-start gap-3" data-testid={`attached-visual-${p.platform}`}>
                      <img src={p.image_url} alt="attached visual" className="w-28 h-28 rounded-md object-cover border border-border" />
                      <div className="flex flex-col gap-1.5">
                        <span className="text-xs font-mono uppercase text-muted-foreground">Attached visual</span>
                        <Button variant="outline" size="sm" data-testid={`download-visual-${p.platform}`} onClick={() => downloadDataUrl(p.image_url)}>
                          <DownloadSimple size={14} className="mr-1" /> Download
                        </Button>
                        <Button variant="ghost" size="sm" className="text-destructive" data-testid={`remove-visual-${p.platform}`} onClick={() => removeVisual(p.id)}>
                          <Trash size={14} className="mr-1" /> Remove
                        </Button>
                      </div>
                    </div>
                  )}

                  {p.audio_url && (
                    <div className="mt-3 flex items-center gap-3" data-testid={`audio-${p.platform}`}>
                      <audio controls src={p.audio_url} className="h-9 flex-1" />
                      <Button variant="outline" size="sm" data-testid={`download-audio-${p.platform}`}
                        onClick={() => { const a = document.createElement("a"); a.href = p.audio_url; a.download = `voiceover-${p.platform}.mp3`; a.click(); }}>
                        <DownloadSimple size={14} className="mr-1" /> MP3
                      </Button>
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-2 mt-4">
                    <Button variant="secondary" data-testid={`approve-${p.platform}`} onClick={() => setStatus(p.id, "approve")}>
                      <CheckCircle size={16} weight="fill" className="mr-1.5" /> Approve
                    </Button>
                    <Button variant="outline" data-testid={`add-visual-${p.platform}`} onClick={() => setVisualFor(p.id)}>
                      <ImageIcon size={16} className="mr-1.5" /> {p.image_url ? "Change visual" : "Add visual"}
                    </Button>
                    <Button variant="outline" data-testid={`voiceover-${p.platform}`} onClick={() => setVoiceFor(p.id)}>
                      <SpeakerHigh size={16} className="mr-1.5" /> {p.audio_url ? "Redo voiceover" : "Voiceover"}
                    </Button>
                    {languages.length > 0 && (
                      <Button variant="outline" data-testid={`translate-${p.platform}`} onClick={() => openTranslate(p.id)}>
                        <Translate size={16} className="mr-1.5" /> Translate
                      </Button>
                    )}
                    <Button variant="outline" data-testid={`schedule-${p.platform}`} onClick={() => openSchedule(p.id)}>
                      <CalendarPlus size={16} className="mr-1.5" /> Schedule
                    </Button>
                    <Button data-testid={`publish-${p.platform}`} onClick={() => setStatus(p.id, "publish")}>
                      <PaperPlaneTilt size={16} weight="fill" className="mr-1.5" /> Publish now
                    </Button>
                    {p.scheduled_at && (
                      <span className="text-xs font-mono text-muted-foreground ml-auto">
                        {new Date(p.scheduled_at).toLocaleString()}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-3">
                    Not connected? Publishing is <strong>simulated</strong>. Connect real accounts on the{" "}
                    <button className="text-primary hover:underline" onClick={() => navigate("/connections")}>Connections</button> page for live auto-publish.
                  </p>
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>

      {/* visual generation dialog */}
      <Dialog open={!!visualFor} onOpenChange={(o) => !o && setVisualFor(null)}>
        <DialogContent data-testid="visual-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Generate a visual for this post</DialogTitle>
            <DialogDescription>Draft a prompt from your post text, pick a model and brand look, then attach a visual.</DialogDescription>
          </DialogHeader>
          <VisualGenerator
            compact
            defaultPrompt=""
            sourceText={drafts[visualFor] || ""}
            onAttach={attachVisual}
          />
          <MediaPickerRow activeId={activeId} onPick={attachMedia} />
        </DialogContent>
      </Dialog>

      {/* voiceover dialog */}
      <Dialog open={!!voiceFor} onOpenChange={(o) => !o && setVoiceFor(null)}>
        <DialogContent data-testid="voice-dialog">
          <DialogHeader>
            <DialogTitle>Generate voiceover</DialogTitle>
            <DialogDescription>Turn this post into narrated audio you can download or attach.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-1.5 block">Voice engine</Label>
              <Select value={voiceProvider} onValueChange={setVoiceProvider}>
                <SelectTrigger data-testid="voice-provider-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="openai" data-testid="voice-provider-openai">OpenAI TTS</SelectItem>
                  <SelectItem value="elevenlabs" data-testid="voice-provider-elevenlabs" disabled={!elevenReady}>
                    ElevenLabs{elevenReady ? "" : " (add API key)"}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            {voiceProvider === "openai" ? (
              <div>
                <Label className="mb-1.5 block">Voice</Label>
                <Select value={openaiVoice} onValueChange={setOpenaiVoice}>
                  <SelectTrigger data-testid="voice-name-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["alloy", "ash", "coral", "echo", "fable", "nova", "onyx", "sage", "shimmer"].map((v) => (
                      <SelectItem key={v} value={v} data-testid={`voice-${v}`} className="capitalize">{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Uses your ElevenLabs default multilingual voice (Rachel).</p>
            )}
            <Button className="w-full" data-testid="voice-generate-btn" onClick={generateVoiceover} disabled={voiceBusy}>
              {voiceBusy ? <><SpinnerGap size={16} className="animate-spin mr-2" /> Generating…</> : <><SpeakerHigh size={16} className="mr-2" /> Generate voiceover</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI clip preview dialog */}
      <Dialog open={!!aiPreview} onOpenChange={(o) => { if (!o) { if (aiPreview?.url) URL.revokeObjectURL(aiPreview.url); setAiPreview(null); } }}>
        <DialogContent data-testid="ai-preview-dialog">
          <DialogHeader>
            <DialogTitle>Your AI video clip</DialogTitle>
            <DialogDescription>Preview the generated clip, then download it if you're happy.</DialogDescription>
          </DialogHeader>
          {aiPreview && (
            <div className="space-y-4">
              <video src={aiPreview.url} controls autoPlay loop playsInline
                data-testid="ai-preview-video" className="w-full max-h-[60vh] rounded-lg bg-black" />
              <Button className="w-full" data-testid="ai-preview-download" onClick={() => {
                const a = document.createElement("a");
                a.href = aiPreview.url; a.download = `ai-clip-${aiPreview.index + 1}-${clipRatio.replace(":", "x")}.mp4`;
                document.body.appendChild(a); a.click(); a.remove();
                toast.success("Downloaded");
              }}>
                <DownloadSimple size={16} className="mr-2" /> Download clip
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* multi-language pack dialog */}
      <Dialog open={packLangOpen} onOpenChange={setPackLangOpen}>
        <DialogContent data-testid="translate-pack-dialog">
          <DialogHeader>
            <DialogTitle>Translate the whole pack</DialogTitle>
            <DialogDescription>Pick languages to translate every post into. Each post keeps its original plus a language switcher, and translations are included in the ZIP export.</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap gap-2 py-2">
            {VOICE_LANGS.filter((l) => l.code !== "auto" && l.code !== "en").map((l) => {
              const on = packLangs.includes(l.code);
              return (
                <button key={l.code} type="button" data-testid={`packlang-${l.code}`}
                  onClick={() => setPackLangs((s) => on ? s.filter((x) => x !== l.code) : [...s, l.code])}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${on ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-accent"}`}>
                  {l.label}
                </button>
              );
            })}
          </div>
          <Button className="w-full" data-testid="translate-pack-run" onClick={translatePack} disabled={packLangBusy || packLangs.length === 0}>
            {packLangBusy ? <><SpinnerGap size={16} className="animate-spin mr-2" /> Translating {packLangs.length} language(s)…</> : <><Translate size={16} className="mr-2" /> Translate pack</>}
          </Button>
        </DialogContent>
      </Dialog>

      {/* translate dialog */}
      <Dialog open={!!translateFor} onOpenChange={(o) => !o && setTranslateFor(null)}>
        <DialogContent data-testid="translate-dialog">
          <DialogHeader>
            <DialogTitle>Translate post</DialogTitle>
            <DialogDescription>Translate this post into another language with DeepL. Copy the result to reuse it.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-1.5 block">Target language</Label>
              <Select value={targetLang} onValueChange={setTargetLang}>
                <SelectTrigger data-testid="translate-lang-select"><SelectValue placeholder="Choose a language" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {languages.map((l) => (
                    <SelectItem key={l.code} value={l.code} data-testid={`translate-lang-${l.code}`}>{l.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button className="w-full" data-testid="translate-run-btn" onClick={runTranslate} disabled={translating || !targetLang}>
              {translating ? <><SpinnerGap size={16} className="animate-spin mr-2" /> Translating…</> : <><Translate size={16} className="mr-2" /> Translate</>}
            </Button>
            {translation && (
              <div className="space-y-2" data-testid="translate-result">
                <div className="flex items-center justify-between">
                  <Label>Translation ({translation.target_lang})</Label>
                  <Button variant="ghost" size="sm" data-testid="translate-copy-btn" onClick={() => copy(translation.text)}>
                    <Copy size={14} className="mr-1" /> Copy
                  </Button>
                </div>
                <div className="rounded-md border border-border bg-background p-3 text-sm whitespace-pre-wrap max-h-64 overflow-y-auto">
                  {translation.text}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* schedule dialog */}
      <Dialog open={!!scheduling} onOpenChange={(o) => !o && setScheduling(null)}>
        <DialogContent data-testid="schedule-dialog">
          <DialogHeader><DialogTitle>Schedule post</DialogTitle></DialogHeader>
          {suggestions.length > 0 && (
            <div>
              <Label className="flex items-center gap-1.5 mb-2"><Clock size={14} className="text-primary" /> Best times for this platform</Label>
              <div className="flex flex-wrap gap-2">
                {suggestions.map((iso) => {
                  const d = new Date(iso);
                  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
                  const active = scheduleAt === local;
                  return (
                    <button key={iso} data-testid={`suggested-time-${iso}`}
                      onClick={() => setScheduleAt(local)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${active ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-accent"}`}>
                      {d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })} · {d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div>
            <Label htmlFor="sched">Date &amp; time</Label>
            <Input id="sched" type="datetime-local" data-testid="schedule-input" value={scheduleAt}
              onChange={(e) => setScheduleAt(e.target.value)} className="mt-1.5" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setScheduling(null)}>Cancel</Button>
            <Button data-testid="confirm-schedule" onClick={doSchedule} disabled={!scheduleAt}>Schedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
